import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { resolveAssetUrl } from "@/lib/api";

const NAV_LINKS = [
  { label: "Home", to: "/", testId: "nav-home-link" },
  { label: "Destinations", to: "/destinations", testId: "nav-destinations-link" },
  { label: "Vehicles", to: "/vehicles", testId: "nav-vehicles-link" },
  { label: "Gallery", to: "/gallery", testId: "nav-gallery-link" },
  { label: "About", to: "/about", testId: "nav-about-link" },
  { label: "Contact", to: "/contact", testId: "nav-contact-link" },
];

const Navbar = ({ settings, transparentOnTop = false }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const isTransparent = transparentOnTop && !scrolled;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        isTransparent
          ? "bg-transparent text-white"
          : "bg-[var(--st-bg)]/95 backdrop-blur-md text-[var(--st-ink)] border-b border-[var(--st-border)]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-brand-link" className="flex items-center gap-3">
          {settings?.logo_url ? (
            <img
              src={resolveAssetUrl(settings.logo_url)}
              alt="Shaurya Travels"
              className="h-10 w-10 object-cover rounded-full border border-current/30"
            />
          ) : (
            <span
              className={`h-10 w-10 rounded-full border flex items-center justify-center font-serif-display text-lg ${
                isTransparent ? "border-white/60" : "border-[var(--st-ink)]/30"
              }`}
            >
              S
            </span>
          )}
          <span className="font-serif-display tracking-widest text-lg sm:text-xl">
            SHAURYA <span className="opacity-60">TRAVELS</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm uppercase tracking-[0.18em]">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              end={l.to === "/"}
              className={({ isActive }) =>
                `nav-link ${isActive ? "active" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/admin/login"
            data-testid="nav-admin-link"
            className={`px-4 py-2 text-xs tracking-[0.2em] uppercase rounded-none border transition-colors duration-300 ${
              isTransparent
                ? "border-white/70 hover:bg-white hover:text-[var(--st-ink)]"
                : "border-[var(--st-ink)] hover:bg-[var(--st-ink)] hover:text-white"
            }`}
          >
            Admin
          </Link>
        </nav>

        <button
          data-testid="mobile-nav-toggle"
          className="md:hidden p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[var(--st-bg)] text-[var(--st-ink)] border-t border-[var(--st-border)]">
          <div className="px-6 py-6 flex flex-col gap-4 text-sm uppercase tracking-[0.18em]">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testId}-mobile`}
                end={l.to === "/"}
                className={({ isActive }) => (isActive ? "text-[var(--st-accent)]" : "")}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/admin/login"
              data-testid="nav-admin-link-mobile"
              className="text-[var(--st-accent)]"
            >
              Admin
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
